// src/features/organisation/hooks/useEmployees.ts
import { useState, useEffect } from 'react';
import { apiClient } from '../../../services/api/client';
import {
  Employee,
  EmployeeCreateDto,
  EmployeeRoleAssignment,
  EmployeeCompetence
} from '../../../types/organisation/employees.types';

export const useEmployees = () => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const data = await apiClient.get('/organisation/employees');
      setEmployees(data);
      setError(null);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  return {
    employees,
    loading,
    error,
    refresh: fetchEmployees
  };
};

export const useEmployee = (id: number | null) => {
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!id) {
      setEmployee(null);
      return;
    }

    const fetchEmployee = async () => {
      try {
        setLoading(true);
        const data = await apiClient.get(`/organisation/employees/${id}`);
        setEmployee(data);
        setError(null);
      } catch (err) {
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    };

    fetchEmployee();
  }, [id]);

  return { employee, loading, error };
};

export const useEmployeeActions = () => {
  const [loading, setLoading] = useState(false);

  const createEmployee = async (data: EmployeeCreateDto): Promise<Employee> => {
    setLoading(true);
    try {
      return await apiClient.post('/organisation/employees', data);
    } finally {
      setLoading(false);
    }
  };

  const updateEmployee = async (id: number, data: EmployeeCreateDto): Promise<Employee> => {
    setLoading(true);
    try {
      return await apiClient.put(`/organisation/employees/${id}`, data);
    } finally {
      setLoading(false);
    }
  };

  const deleteEmployee = async (id: number): Promise<void> => {
    setLoading(true);
    try {
      await apiClient.delete(`/organisation/employees/${id}`);
    } finally {
      setLoading(false);
    }
  };

  return {
    createEmployee,
    updateEmployee,
    deleteEmployee,
    loading
  };
};